import type { CurrentUser, SetProductsInput, UpdateAccountInput } from '@bb/shared';
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiRequestError, api } from '@/lib/api';
import { changeLocale } from '@/lib/i18n';

export const currentUserQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: async (): Promise<CurrentUser | null> => {
    try {
      return await api<CurrentUser>('/auth/me');
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
});

export function useCurrentUser() {
  return useQuery(currentUserQuery);
}

/** Loader helper: resolves the user or returns null without throwing. */
export function loadCurrentUser(queryClient: QueryClient): Promise<CurrentUser | null> {
  return queryClient.ensureQueryData(currentUserQuery);
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAccountInput) =>
      api<CurrentUser>('/v1/account', { method: 'PATCH', body: input }),
    onSuccess: async (user) => {
      queryClient.setQueryData(currentUserQuery.queryKey, user);
      await changeLocale(user.locale);
    },
  });
}

export function useSetProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetProductsInput) =>
      api<CurrentUser>('/v1/account/products', { method: 'PUT', body: input }),
    onSuccess: (user) => queryClient.setQueryData(currentUserQuery.queryKey, user),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.setQueryData(currentUserQuery.queryKey, null);
      window.location.assign('/');
    },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: (confirmEmail: string) =>
      api<void>('/v1/account', { method: 'DELETE', body: { confirmEmail } }),
    onSuccess: () => window.location.assign('/'),
  });
}
